import React from 'react';
import PropTypes from 'prop-types';
import R from 'ramda';
import OptionalSection from '@cotyorg/ccx-utils/js/components/OptionalSection';
import uuid from '@cotyorg/ccx-utils/js/helpers/uuid';
import IconButton from '@cotyorg/ccx-molecules/IconButton';
import Heading from '@cotyorg/ccx-atoms/Heading';
import ShowHide from '@cotyorg/ccx-utils/js/components/ShowHide';
import Retailer from './Retailer';

const VISIBLE_RETAILERS = 3;

const generateToggleIconName = ({ isOpen }) =>
  isOpen
    ? '/ccx-files/assets/chevron-white-up.svg'
    : '/ccx-files/assets/chevron-white-down.svg';

const getCurrentSwatchRetailers = ({ selectedSKU, retailers }) => {
  if (!retailers) {
    return [];
  }

  // ! TODO Fix SKU/EAN substring transformation inside Product importer
  const currentRetailers = retailers.find(
    ({ sku }) => sku.replace(/^0*/, '') === selectedSKU.replace(/^0*/, '')
  );

  return R.propOr([], 'retailersList', currentRetailers);
};

const ProductRetailers = ({
  retailersHeadline,
  buyButtonLabel,
  retailers,
  toggleMoreRetailers,
  showMoreRetailersLabel,
  showLessRetailersLabel,
  outOfStockLabel,
  isOpen,
  selectedSKU = '',
  productTitle,
  categoryTitle,
  subcategoryTitle,
  productSwatch,
  customRetailer,
  hidePrice,
}) => {
  const currentSwatchRetailers = getCurrentSwatchRetailers({
    selectedSKU,
    retailers,
  });

  return (
    <>
      <OptionalSection
        renderCondition={!!currentSwatchRetailers.length}
        section={() => (
          <div className="product-retailers">
            <Heading level={5} extraClass="product-retailers__heading">
              {retailersHeadline}
            </Heading>

            <ul className="product-retailers__list">
              {R.take(VISIBLE_RETAILERS, currentSwatchRetailers).map(
                (retailer) => (
                  <Retailer
                    key={uuid()}
                    name={R.prop('name', retailer)}
                    currency={R.prop('currencySymbol', retailer)}
                    currencyCode={R.prop('currencyCode', retailer)}
                    price={R.prop('price', retailer)}
                    trackingPrice={R.prop('trackingPrice', retailer)}
                    link={R.prop('deepLinkUrl', retailer)}
                    inStock={R.prop('inStock', retailer)}
                    sku={selectedSKU}
                    buyButtonLabel={buyButtonLabel}
                    outOfStockLabel={outOfStockLabel}
                    productTitle={productTitle}
                    categoryTitle={categoryTitle}
                    subcategoryTitle={subcategoryTitle}
                    productSwatch={productSwatch}
                    hidePrice={hidePrice}
                  />
                )
              )}
            </ul>

            <ShowHide
              isOpen={isOpen}
              extraClass="product-retailers__show-hide"
              noTransitionClass="product-retailers__show-hide--no-transition"
            >
              <ul className="product-retailers__list">
                {R.takeLast(
                  currentSwatchRetailers.length - VISIBLE_RETAILERS,
                  currentSwatchRetailers
                ).map((retailer) => (
                  <Retailer
                    key={uuid()}
                    name={R.prop('name', retailer)}
                    currency={R.prop('currencySymbol', retailer)}
                    currencyCode={R.prop('currencyCode', retailer)}
                    price={R.prop('price', retailer)}
                    trackingPrice={R.prop('trackingPrice', retailer)}
                    link={R.prop('deepLinkUrl', retailer)}
                    inStock={R.prop('inStock', retailer)}
                    sku={selectedSKU}
                    buyButtonLabel={buyButtonLabel}
                    outOfStockLabel={outOfStockLabel}
                    productTitle={productTitle}
                    categoryTitle={categoryTitle}
                    subcategoryTitle={subcategoryTitle}
                    hidePrice={hidePrice}
                  />
                ))}
              </ul>
            </ShowHide>

            {currentSwatchRetailers.length > VISIBLE_RETAILERS && (
              <>
                <IconButton
                  iconAfterText
                  extraClass="product-retailers__show-hide-button"
                  iconExtraClass="product-retailers__show-hide-icon"
                  iconSrc={generateToggleIconName({ isOpen })}
                  onClick={toggleMoreRetailers}
                >
                  {isOpen ? showLessRetailersLabel : showMoreRetailersLabel}
                </IconButton>
              </>
            )}
          </div>
        )}
      />

      <OptionalSection
        renderCondition={!R.isNil(customRetailer)}
        section={() => (
          <div className="product-retailers">
            <Heading level={5} extraClass="product-retailers__heading">
              Retailer
            </Heading>

            <Retailer
              key={uuid()}
              inStock
              name={R.path(['retailer', 'name'], customRetailer)}
              currency={R.path(['retailer', 'currency'], customRetailer)}
              currencyCode={R.path(
                ['retailer', 'currencyCode'],
                customRetailer
              )}
              link={R.path(['button', 'link'], customRetailer)}
              sku={selectedSKU}
              buyButtonLabel={R.path(['button', 'title'], customRetailer)}
              productTitle={productTitle}
              categoryTitle={categoryTitle}
              subcategoryTitle={subcategoryTitle}
              productSwatch={productSwatch}
              hidePrice={hidePrice}
            />
          </div>
        )}
      />
    </>
  );
};

ProductRetailers.propTypes = {
  retailers: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      price: PropTypes.string,
      currency: PropTypes.string,
    })
  ),
  retailersHeadline: PropTypes.string,
  buyButtonLabel: PropTypes.string,
  toggleMoreRetailers: PropTypes.func.isRequired,
  isOpen: PropTypes.bool,
  showMoreRetailersLabel: PropTypes.string,
  showLessRetailersLabel: PropTypes.string,
  selectedSKU: PropTypes.string,
  outOfStockLabel: PropTypes.string,
  productTitle: PropTypes.string,
  categoryTitle: PropTypes.string,
  subcategoryTitle: PropTypes.string,
  productSwatch: PropTypes.shape({
    swatches: PropTypes.arrayOf(
      PropTypes.shape({
        sku: PropTypes.string,
        itemName: PropTypes.string,
        skuColor: PropTypes.string,
      })
    ),
  }),
  customRetailer: PropTypes.shape({
    button: {
      title: PropTypes.string,
      link: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
    },
    retailer: {
      name: PropTypes.string.isRequired,
      currencyCode: PropTypes.string,
    },
  }),
  hidePrice: PropTypes.bool,
};

export default ProductRetailers;
